import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { createFournisseur, updateFournisseur, getFournisseurById } from "../Services/AchatApi";
import { useOrganisations } from "../Auth/useOrganisations";
import { useUser } from "../Auth/useUser";

const AddFournisseurModal = ({ isOpen, onClose, onSuccess, fournisseurId = null }) => {
  const user = useUser();
  const infoUser = user.result;
  const [, , usedOrganisations] = useOrganisations();
  
  const [formData, setFormData] = useState({
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    contact: "",
    siret: "",
    matricule_fiscal: "",
    registre_commerce: "",
    numero_tva: "",
    rib: "",
    iban: "",
    bic: "",
    nom_banque: "",
    conditions_paiement: "comptant",
    delai_paiement: 0,
    statut: "actif",
  });
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const isEditMode = !!fournisseurId;

  // Fetch supplier data when in edit mode
  useEffect(() => {
    if (isOpen && fournisseurId) {
      fetchFournisseur();
    } else if (isOpen && !fournisseurId) {
      // Reset form for create mode
      resetForm();
    }
  }, [isOpen, fournisseurId]);

  const fetchFournisseur = async () => {
    setFetching(true);
    try {
      const response = await getFournisseurById(fournisseurId);
      if (response.data.success) {
        const supplier = response.data.data;
        setFormData({
          nom: supplier.nom || "",
          telephone: supplier.telephone || "",
          email: supplier.email || "",
          adresse: supplier.adresse || "",
          contact: supplier.contact || "",
          siret: supplier.siret || "",
          matricule_fiscal: supplier.matricule_fiscal || "",
          registre_commerce: supplier.registre_commerce || "",
          numero_tva: supplier.numero_tva || "",
          rib: supplier.banque?.rib || "",
          iban: supplier.banque?.iban || "",
          bic: supplier.banque?.bic || "",
          nom_banque: supplier.banque?.nom_banque || "",
          conditions_paiement: supplier.conditions_paiement || "comptant",
          delai_paiement: supplier.delai_paiement || 0,
          statut: supplier.statut || "actif",
        });
      }
    } catch (error) {
      console.error("Error fetching supplier:", error);
      toast.error("Erreur lors du chargement du fournisseur");
      onClose();
    } finally {
      setFetching(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      telephone: "",
      email: "",
      adresse: "",
      contact: "",
      siret: "",
      matricule_fiscal: "",
      registre_commerce: "",
      numero_tva: "",
      rib: "",
      iban: "",
      bic: "",
      nom_banque: "",
      conditions_paiement: "comptant",
      delai_paiement: 0,
      statut: "actif",
    });
  };

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom.trim()) {
      toast.error("Le nom du fournisseur est obligatoire");
      return;
    }

    setLoading(true);

    const supplierData = {
      organisation_id: usedOrganisations,
      nom: formData.nom,
      telephone: formData.telephone || "",
      email: formData.email || "",
      adresse: formData.adresse || "",
      contact: formData.contact || "",
      siret: formData.siret || "",
      matricule_fiscal: formData.matricule_fiscal || "",
      registre_commerce: formData.registre_commerce || "",
      numero_tva: formData.numero_tva || "",
      banque: {
        rib: formData.rib || "",
        iban: formData.iban || "",
        bic: formData.bic || "",
        nom_banque: formData.nom_banque || "",
      },
      conditions_paiement: formData.conditions_paiement,
      delai_paiement: parseInt(formData.delai_paiement) || 0,
      statut: formData.statut,
      ...(isEditMode && { updated_by: infoUser.id }),
      ...(!isEditMode && { created_by: infoUser.id }),
    };

    try {
      let response;
      
      if (isEditMode) {
        response = await updateFournisseur(fournisseurId, supplierData);
      } else {
        response = await createFournisseur(supplierData);
      }
      
      if (response.data.success) {
        toast.success(isEditMode ? "Fournisseur modifié avec succès" : "Fournisseur créé avec succès");
        onSuccess();
        onClose();
        if (!isEditMode) {
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error(
        error.response?.data?.message ||
          (isEditMode ? "Erreur lors de la modification" : "Erreur lors de la création")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? "Modifier le Fournisseur" : "Nouveau Fournisseur"}
            </h5>
            <button
              type="button"
              className="close"
              onClick={onClose}
              disabled={loading || fetching}
            >
              <span>&times;</span>
            </button>
          </div>
          
          {fetching ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Chargement...</span>
              </div>
              <p className="mt-2">Chargement du fournisseur...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {/* Status field for edit mode */}
                {isEditMode && (
                  <div className="form-group">
                    <label>Statut</label>
                    <select
                      className="form-control"
                      value={formData.statut}
                      onChange={(e) => handleChange("statut", e.target.value)}
                    >
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                      <option value="bloqué">Bloqué</option>
                    </select>
                  </div>
                )}

                {/* Basic Info */}
                <div className="form-group">
                  <label>Nom du fournisseur *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.nom}
                    onChange={(e) => handleChange("nom", e.target.value)}
                    required
                  />
                </div>

                {/* Tunisian Fiscal Information */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Matricule Fiscal</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.matricule_fiscal}
                        onChange={(e) => handleChange("matricule_fiscal", e.target.value)}
                        placeholder="1234567X/A/M/000"
                      />
                      <small className="text-muted">Format Tunisien</small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Registre de Commerce (RC)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.registre_commerce}
                        onChange={(e) => handleChange("registre_commerce", e.target.value)}
                        placeholder="B1234567890"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Numéro TVA</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.numero_tva}
                        onChange={(e) => handleChange("numero_tva", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>SIRET (optionnel)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.siret}
                        onChange={(e) => handleChange("siret", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Contact</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.contact}
                    onChange={(e) => handleChange("contact", e.target.value)}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.telephone}
                        onChange={(e) => handleChange("telephone", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Adresse</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={formData.adresse}
                    onChange={(e) => handleChange("adresse", e.target.value)}
                  />
                </div>

                {/* Bank Details */}
                <div className="card mt-3">
                  <div className="card-header bg-light">
                    <strong>Coordonnées Bancaires</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>RIB</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.rib}
                            onChange={(e) => handleChange("rib", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>IBAN</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.iban}
                            onChange={(e) => handleChange("iban", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>BIC/SWIFT</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.bic}
                            onChange={(e) => handleChange("bic", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Nom de la banque</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.nom_banque}
                            onChange={(e) => handleChange("nom_banque", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Terms */}
                <div className="row mt-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Conditions de paiement</label>
                      <select
                        className="form-control"
                        value={formData.conditions_paiement}
                        onChange={(e) => handleChange("conditions_paiement", e.target.value)}
                      >
                        <option value="comptant">Comptant</option>
                        <option value="30_jours">30 jours</option>
                        <option value="60_jours">60 jours</option>
                        <option value="90_jours">90 jours</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Délai de paiement (jours)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.delai_paiement}
                        onChange={(e) => handleChange("delai_paiement", e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      {isEditMode ? "Modification..." : "Création..."}
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save mr-2"></i>
                      {isEditMode ? "Modifier" : "Enregistrer"}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFournisseurModal;